import { type FormEvent, useState } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import { useChangeMyEmail, useUpdateMyProfile } from '@/hooks/useUsers';
import { useChangePassword } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

const MIN_PASSWORD_LENGTH = 8;

/** First message the API returned for one request field (validation errors are keyed by camelCase field name). */
function apiFieldError(error: unknown, field: string): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const errors = (error.response?.data as { errors?: Record<string, string[]> } | undefined)?.errors;
  return errors?.[field]?.[0];
}

/**
 * "My account" - open to every signed-in user (competitor, judge, organizer, admin):
 * name, login email and password. Email and password changes ask for the current
 * password, so someone borrowing an unlocked session cannot take the account over.
 */
export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);

  // --- name ---
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const updateProfile = useUpdateMyProfile();

  // --- email ---
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const changeEmail = useChangeMyEmail();

  // --- password ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const changePassword = useChangePassword();
  const [passwordClientError, setPasswordClientError] = useState<string | undefined>();

  if (!user) return null;

  // Keep the in-memory session's user record in sync with an edit.
  function syncUser(updated: Partial<typeof user>) {
    if (accessToken && user) {
      setSession({ accessToken, user: { ...user, ...updated } });
    }
  }

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    const updated = await updateProfile.mutateAsync({ firstName: firstName.trim(), lastName: lastName.trim() }).catch(() => null);
    if (updated) syncUser(updated);
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    const updated = await changeEmail.mutateAsync({ newEmail: newEmail.trim(), currentPassword: emailPassword }).catch(() => null);
    if (updated) {
      syncUser(updated);
      setNewEmail('');
      setEmailPassword('');
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setPasswordClientError(undefined);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordClientError(`Novo geslo mora imeti vsaj ${MIN_PASSWORD_LENGTH} znakov.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordClientError('Novi gesli se ne ujemata.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordClientError('Novo geslo mora biti drugačno od trenutnega.');
      return;
    }
    const ok = await changePassword.mutateAsync({ currentPassword, newPassword }).then(() => true).catch(() => false);
    if (ok) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }

  const emailCurrentPasswordError = apiFieldError(changeEmail.error, 'currentPassword') && 'Trenutno geslo ni pravilno.';
  const emailTakenError = apiFieldError(changeEmail.error, 'newEmail') && 'Račun s tem e-naslovom že obstaja.';
  const passwordCurrentError = apiFieldError(changePassword.error, 'currentPassword') && 'Trenutno geslo ni pravilno.';
  const emailGenericError =
    changeEmail.isError && !emailCurrentPasswordError && !emailTakenError ? getApiErrorMessage(changeEmail.error) : undefined;
  const passwordGenericError =
    changePassword.isError && !passwordCurrentError ? getApiErrorMessage(changePassword.error) : undefined;

  return (
    <div className="flex max-w-lg flex-col gap-4">
      <h1 className="text-xl font-semibold text-fg">Moj račun</h1>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">Osebni podatki</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleProfileSubmit} className="flex flex-col gap-4">
            {updateProfile.isError && <Alert variant="error">{getApiErrorMessage(updateProfile.error)}</Alert>}
            {updateProfile.isSuccess && <Alert variant="success">Podatki so shranjeni.</Alert>}
            <div className="grid grid-cols-2 gap-3">
              <Input label="Ime" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" required />
              <Input label="Priimek" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" required />
            </div>
            <Button
              type="submit"
              isLoading={updateProfile.isPending}
              disabled={!firstName.trim() || !lastName.trim()}
              className="self-start"
            >
              Shrani spremembe
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">E-naslov za prijavo</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
            {emailGenericError && <Alert variant="error">{emailGenericError}</Alert>}
            {changeEmail.isSuccess && <Alert variant="success">E-naslov je spremenjen. Z njim se prijavite naslednjič.</Alert>}
            <Input label="Trenutni e-naslov" value={user.email} disabled />
            <Input
              label="Nov e-naslov"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              error={emailTakenError || undefined}
              autoComplete="off"
              required
            />
            <Input
              label="Trenutno geslo (potrditev)"
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              error={emailCurrentPasswordError || undefined}
              autoComplete="current-password"
              required
            />
            <Button
              type="submit"
              isLoading={changeEmail.isPending}
              disabled={!newEmail.trim() || !emailPassword}
              className="self-start"
            >
              Spremeni e-naslov
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">Geslo</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
            {passwordGenericError && <Alert variant="error">{passwordGenericError}</Alert>}
            {changePassword.isSuccess && (
              <Alert variant="success">Geslo je spremenjeno. Vse druge seje so bile odjavljene.</Alert>
            )}
            <Input
              label="Trenutno geslo"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={passwordCurrentError || undefined}
              autoComplete="current-password"
              required
            />
            <Input
              label="Novo geslo"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <Input
              label="Ponovite novo geslo"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={passwordClientError}
              autoComplete="new-password"
              required
            />
            <Button
              type="submit"
              isLoading={changePassword.isPending}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              className="self-start"
            >
              Spremeni geslo
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
